import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Range } from 'slate';
import { useFocused, useSlate } from 'slate-react';
import { isSelectionValid } from '../text-editor.helper';
import { MainToolbar } from './main.toolbar';

const Portal = ({ children }) =>
  typeof document === 'object' ? ReactDOM.createPortal(children, document.body) : null;

const GAP_ABOVE_INPUT = 6;

function positionToolbar(el, anchorEl) {
  if (!el || !anchorEl) return;
  const rect = anchorEl.getBoundingClientRect();
  el.style.top = `${rect.top + window.pageYOffset - el.offsetHeight - GAP_ABOVE_INPUT}px`;
  el.style.left = `${rect.left + window.pageXOffset + rect.width / 2 - el.offsetWidth / 2}px`;
}

export const HoveringToolbar = ({
  toolbarRef,
  inputContainerRef,
  toolbar,
  onOpenLinkModal,
  uploadAttachments
}) => {
  const ref = useRef(null);
  const [toolbarHasFocus, setToolbarHasFocus] = useState(false);
  const setRef = (el) => {
    ref.current = el;
    if (toolbarRef) toolbarRef.current = el;
  };
  const editor = useSlate();
  const inFocus = useFocused();
  const { selection } = editor;

  // Keep toolbar visible when focus is on a toolbar control (e.g. font-size select) so dropdown can open
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onFocusIn = () => setToolbarHasFocus(true);
    const onFocusOut = () => setToolbarHasFocus(false);
    el.addEventListener('focusin', onFocusIn);
    el.addEventListener('focusout', onFocusOut);
    return () => {
      el.removeEventListener('focusin', onFocusIn);
      el.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  // Show based on onSelection: 'selection' = only when text selected; 'focus' = when focused (cursor or selection)
  useEffect(() => {
    const el = ref.current;
    const anchorEl = inputContainerRef?.current;
    const hasValidSelection = anchorEl && selection && isSelectionValid(editor);
    const hasSelectionRange = hasValidSelection && !Range.isCollapsed(selection);
    const focusInToolbar = el && document.activeElement && el.contains(document.activeElement);
    const editorOrToolbarFocused = inFocus || toolbarHasFocus || focusInToolbar;
    const visible =
      hasValidSelection &&
      editorOrToolbarFocused &&
      (toolbar?.onSelection ? hasSelectionRange : true);

    if (!el) return;
    if (!visible) {
      if (toolbarHasFocus) setToolbarHasFocus(false);
      el.style.opacity = '0';
      el.style.top = '-10000px';
      el.style.left = '-10000px';
      return;
    }
    el.style.opacity = '1';
    el.style.zIndex = String(toolbar?.zIndex || 1);

    positionToolbar(el, anchorEl);
  }, [
    editor,
    inFocus,
    inputContainerRef,
    toolbarHasFocus,
    toolbar?.onSelection,
    toolbar?.zIndex,
    selection
  ]);

  // Re-position on window scroll and resize
  useEffect(() => {
    const anchorEl = inputContainerRef?.current;
    if (!ref.current || !anchorEl) return;
    const onMove = () => {
      const el = ref.current;
      if (el?.style.opacity === '1') positionToolbar(el, inputContainerRef?.current);
    };
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [inputContainerRef]);

  const handleToolbarMouseDown = (e) => {
    const tag = e.target?.tagName?.toUpperCase();
    if (tag !== 'SELECT' && tag !== 'INPUT') e.preventDefault();
  };

  return (
    <Portal>
      <div ref={setRef} className="text-editor__toolbar" onMouseDown={handleToolbarMouseDown}>
        <MainToolbar
          toolbar={toolbar}
          buttonClassName="text-editor__toolbar-button"
          onOpenLinkModal={onOpenLinkModal}
          uploadAttachments={uploadAttachments}
        />
      </div>
    </Portal>
  );
};
